import React, { useMemo } from 'react';

import { orderBy } from 'lodash';
import { Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useDebounce } from 'hooks/useDebaunce';
import useHashmapConverter from 'hooks/useHashmapConverter';

import { useQueryGetSingleWbs, useQueryGetWbs } from 'api/queries/getWbs.query';

import { mapper } from 'components/common/GlobalFilterComponent/components/WbsFilter/utils';
import { OptionValue } from 'components/common/GlobalFilterComponent/components/WbsFilter/wbsFilter';
import { WbsOption } from 'components/common/MyCustomSelect/components/WbsOption/WbsOption';

import { ProgramStatusUrlParams } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramStatusSummaryReport/programStatusSummaryReportTypes';
import { Select } from 'components/DesignSystem/Select';

export const WbsSelect = ({ methods, name }) => {
    const hashcode = Object.values(useParams<ProgramStatusUrlParams>()).filter(Boolean).join(',');
    const { versionList: versionFromHashMap } = useHashmapConverter(hashcode);
    const latestVersionId = versionFromHashMap[0]?.versionDbId;

    const [filter, setFilter] = React.useState('');
    const debouncedValue = useDebounce<string>(filter, 1000);

    const { data: wbs, isFetching } = useQueryGetWbs({
        latestVersion: latestVersionId,
        filter: debouncedValue,
    });

    const options = useMemo<ISelectOption<OptionValue>[]>(
        () => (wbs ? orderBy(wbs, [(item: any) => item.activity_id.toLowerCase()], ['asc']).map(mapper) : []),
        [wbs],
    );

    const { data: singleWbs } = useQueryGetSingleWbs({
        latestVersion: latestVersionId,
        taskId: methods?.getValues(name), // make sure BE can perform search by task_version_hash_code
    });

    const handleInputChangePrimary = (inputText, event) => {
        if (event.action !== 'input-blur' && event.action !== 'menu-close' && event.action !== 'set-value') {
            setFilter(inputText);
        }
        if (event.action === 'menu-close') {
            setFilter('');
        }
    };

    const handleChange = (option: ISelectOption<OptionValue> | null) => {
        methods?.setValue(name, option ? option.value.value : null, { shouldDirty: true });
    };

    const handleValue = (value: number | undefined) => {
        if (!value) {
            return null;
        }
        if (singleWbs && singleWbs.length > 0 && mapper(singleWbs[0]).value.value === String(value)) {
            return mapper(singleWbs[0]);
        }

        return wbs?.map(mapper).find((item) => item.value.value === String(value)) || null;
    };
    return (
        <Controller
            render={({ field }) => (
                <Select<ISelectOption<OptionValue>>
                    {...field}
                    options={options}
                    onInputChange={handleInputChangePrimary}
                    isClearable={true}
                    isLoading={isFetching}
                    onChange={handleChange}
                    value={handleValue(field.value)}
                    placeholder={'Select WBS or start typing its name...'}
                    components={{
                        Option: WbsOption,
                    }}
                />
            )}
            name={name}
            control={methods?.control}
        />
    );
};
